/**
 * "Best estate with cashflow covered" — the goal advisers actually decide with, as one rule for
 * every module (owner, 24 Sep 2026: "I'd be going with the strategy that allows me to optimise my
 * estate whilst achieving my cashflow goals").
 *
 * WHY A FOURTH GOAL. "Best cashflow" as a pure ranking always rewards the plan that converts the
 * most capital into income — which is exactly the nil-residual lifetime annuity — and "best estate"
 * alone can crown a plan the household cannot live on. The question a family asks is "which plan
 * leaves the most, given that we still have to pay the bills". This answers it.
 *
 * THE THREE-STEP RULE (agreed with the owner, 24 Sep 2026), so the chip is never blank and never
 * quietly becomes "best cashflow":
 *   1. COVERED — among the strategies whose yearly cashflow meets the target (zero, or the surplus
 *      the client set), the best estate.
 *   2. CARRIED — if none does (common in aged care, where a daily payment on the room nearly always
 *      leaves a shortfall), among the strategies whose shortfall the savings carry for the WHOLE
 *      projection period, the best estate. The same runway the plan summary shows: a plan that runs
 *      the money out inside the period is not covered in any sense a family would accept.
 *   3. LONGEST — if nothing survives even that, the strategy that lasts longest, badged as such
 *      rather than presented as a recommendation. The card says which case applies.
 *
 * Reads only what every engine already puts on a strategy: the year-1 cashflow in its snapshot and
 * its projection rows (`unfundedShortfall`, the engine's own "the money has run out" flag). No
 * figure is re-derived.
 */
import { summariseFunding, type FundingProjectionYear } from '../components/insights/fundingSummary.tsx';

export const CASHFLOW_COVERED_LABEL = 'Optimal Legacy (Cashflow Covered)';

export type CashflowCoveredBasis = 'covered' | 'carried' | 'longest';

export interface CashflowCoveredVerdict {
    basis: CashflowCoveredBasis;
    /** The plan's year-1 cashflow. Negative is a shortfall the savings carry. */
    cashflow: number;
    /** The year the money runs out, or null when it lasts the period. */
    runsOutYear: number | null;
}

/** The least a strategy has to carry for the rule to read it. */
export interface CoverableStrategy {
    name?: string;
    snapshot?: { netCashflow?: number; cashflow?: number; cashFlow?: number; legacyValue?: number };
    projections?: unknown;
}

export interface CashflowCoveredOptions {
    /** The yearly surplus the plan must keep. Zero means "the bills are paid". */
    cashflowTarget?: number;
}

const cashflowOf = (s: CoverableStrategy): number => {
    const snap = s.snapshot || {};
    const v = snap.netCashflow ?? snap.cashflow ?? snap.cashFlow;
    return Number.isFinite(Number(v)) ? Number(v) : -Infinity;
};
const legacyOf = (s: CoverableStrategy): number => {
    const v = Number(s.snapshot?.legacyValue);
    return Number.isFinite(v) ? v : -Infinity;
};
const runsOutYearOf = (s: CoverableStrategy): number | null => {
    const rows = Array.isArray(s.projections) ? (s.projections as FundingProjectionYear[]) : [];
    if (rows.length === 0) return null;
    const summary = summariseFunding(rows) as { moneyRunsOutYear?: number | null };
    return summary.moneyRunsOutYear ?? null;
};
const bestLegacy = <S extends CoverableStrategy>(pool: S[]): S | null =>
    pool.reduce<S | null>((best, s) => (best == null || legacyOf(s) > legacyOf(best) ? s : best), null);

/**
 * The leader for the goal, with the case it was chosen under. Null only for an empty field.
 * `eligible` is the field the ranking may crown (a flagged annuity is not in it); `runwayField`
 * is the same unless a caller has a reason to widen it.
 */
export function pickCashflowCoveredLeader<S extends CoverableStrategy>(
    eligible: S[],
    options: CashflowCoveredOptions = {},
): { strategy: S; verdict: CashflowCoveredVerdict } | null {
    const target = Number(options.cashflowTarget) || 0;
    const field = eligible.filter((s) => !!s?.name);
    if (field.length === 0) return null;

    // 1. Covered.
    const covered = field.filter((s) => cashflowOf(s) >= target);
    const c = bestLegacy(covered);
    if (c) return { strategy: c, verdict: { basis: 'covered', cashflow: cashflowOf(c), runsOutYear: runsOutYearOf(c) } };

    // 2. Carried: the shortfall lasts the whole period. Needs projection rows to say so.
    const carried = field.filter((s) => Array.isArray(s.projections) && (s.projections as unknown[]).length > 0 && runsOutYearOf(s) == null);
    const k = bestLegacy(carried);
    if (k) return { strategy: k, verdict: { basis: 'carried', cashflow: cashflowOf(k), runsOutYear: null } };

    // 3. Longest: the money runs out in every plan; the latest year wins, estate breaking ties.
    const withYear = field.map((s) => ({ s, year: runsOutYearOf(s) }));
    const longest = withYear.reduce<{ s: S; year: number | null } | null>((best, cur) => {
        if (best == null) return cur;
        const by = best.year ?? Number.POSITIVE_INFINITY;
        const cy = cur.year ?? Number.POSITIVE_INFINITY;
        if (cy > by) return cur;
        if (cy === by && legacyOf(cur.s) > legacyOf(best.s)) return cur;
        return best;
    }, null);
    if (!longest) return null;
    return { strategy: longest.s, verdict: { basis: 'longest', cashflow: cashflowOf(longest.s), runsOutYear: longest.year } };
}

/** The card's line for the case the leader was chosen under. */
export function cashflowCoveredNote(v: CashflowCoveredVerdict, horizonYears: number, formatCurrency: (n: number) => string): string {
    if (v.basis === 'covered') return v.cashflow > 0 ? `Cashflow covered: ${formatCurrency(v.cashflow)} a year to spare.` : 'Cashflow covered.';
    if (v.basis === 'carried') return `Shortfall of ${formatCurrency(Math.abs(v.cashflow))} a year, carried by savings for the full ${horizonYears} years.`;
    return `No strategy keeps the money going for the full ${horizonYears} years — this one lasts longest${v.runsOutYear != null ? ` (to year ${v.runsOutYear})` : ''}. Shown so you can see the least damaging option, not as a recommendation.`;
}

/** The chip's words, the same in every module. */
export const CASHFLOW_COVERED_CHIP = 'Best estate, cashflow covered';

/**
 * ONE WORD FOR THE ESTATE GOAL (owner, 24 Sep 2026: "rename to best estate"). "Legacy" is adviser
 * language; every client-facing chip and badge says "estate". The engine's internal labels
 * ("Optimal Legacy") are unchanged — they are keys, not words a client reads.
 */
export const BEST_ESTATE_CHIP = 'Best estate';
export const GOAL_CHIP_LABELS: Record<string, string> = {
    [CASHFLOW_COVERED_LABEL]: CASHFLOW_COVERED_CHIP,
    'Optimal Legacy': BEST_ESTATE_CHIP,
    'Optimal Cashflow': 'Best cashflow',
    'Optimal Age Pension': 'Best Age Pension',
};
/** The words a client sees for an engine label; anything else passes through untouched. */
export const friendlyGoalLabel = (label: unknown): string => GOAL_CHIP_LABELS[String(label ?? '')] ?? String(label ?? '');

/**
 * Whether the cashflow-covered leader IS the best-estate leader. Owner (24 Sep 2026): keep both
 * chips, because a client may genuinely want the biggest estate whatever the cashflow — but when
 * the two are the same plan, say so rather than offer a second tab that shows the same card.
 */
export function coveredIsBestEstate(recommended: ReadonlyArray<{ label?: string; strategy?: { name?: string } | null }> | null | undefined): boolean {
    const list = recommended || [];
    const covered = list.find((r) => r.label === CASHFLOW_COVERED_LABEL)?.strategy?.name;
    const estate = list.find((r) => r.label === 'Optimal Legacy')?.strategy?.name;
    return !!covered && !!estate && covered === estate;
}
export const SAME_PLAN_NOTE = 'Same result as Best estate — no plan leaves more.';
